import { FC, ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import Typography from '../Typography';
import Grid from '../Grid';
import Box from '../Box';
import IconButton from '../IconButton';
import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';
import Stack from '@mui/material/Stack';

export interface PageHeaderProps {
  /**
   * Right side tools
   */
  children?: ReactNode | string;
  /**
   * If a string, it is converted to a H1. If you need a customized heading, you can provide a React component instead.
   */
  heading: ReactNode | string;
  /**
   * If a string, it is converted to secondary text.
   */
  perex?: ReactNode | string;
  /**
   * Backlink description is displayed as a simple tooltip popup. Must be set to show the backlink button. You can override this behavior with showBacklink prop.
   */
  backlinkText?: string;
  /**
   * OnClick event handler for backlink. Must be set to show the backlink button. You can override this behavior with showBacklink prop.
   */
  onBacklinkClick?: () => void;
  /**
   * Whether you want to display backlink or not. If `backlinkText` with `onBacklinkClick` are set, backlink is automatically shown, but you can override this behavior by setting showBacklink to `false`.
   */
  showBacklink?: boolean;
  /**
   * OnClick handler for edit icon. Must be set to show the edit icon. You can override this behavior with the showEditIcon prop.
   */
  onEdit?: () => void;
  /**
   * Whether you want to display an edit icon or not. If `onEdit` is set, the edit icon is automatically shown, but you can override this behavior using this prop.
   */
  showEditIcon?: boolean;
}

const PageHeaderRoot = styled(Box, {
  name: 'O2PageHeader',
  slot: 'Root',
})(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  marginLeft: theme.spacing(3),
  marginRight: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
  },
}));

const PageHeaderPerex = styled('div', {
  name: 'O2PageHeader',
  slot: 'Perex',
})(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const PageHeaderBacklink = styled(IconButton, {
  name: 'O2PageHeader',
  slot: 'Backlink',
})(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const PageHeaderEditIconButton = styled(IconButton, {
  name: 'O2PageHeader',
  slot: 'EditIcon',
})(({ theme }) => ({
  color: theme.palette.text.primary,
  marginTop: 4,
}));

/**
 * This header belongs to a specific page. Therefor it's a page header, not application header.
 *
 * @deprecated Use `@o2/common-business/Subheader` instead.
 */
const PageHeader: FC<PageHeaderProps> = (props) => {
  const heading =
    typeof props.heading === 'string' ? <Typography variant="h1">{props.heading}</Typography> : props.heading;

  const perex = props.perex ? <PageHeaderPerex>{props.perex}</PageHeaderPerex> : null;

  const showBacklink = isBoolean(props.showBacklink)
    ? props.showBacklink
    : !isNil(props.backlinkText) && !isNil(props.onBacklinkClick);

  const backlink = showBacklink ? (
    <Box marginRight={2}>
      <PageHeaderBacklink title={props.backlinkText} onClick={props.onBacklinkClick} size="small" variant="circle">
        <ArrowBackIcon fontSize="large" />
      </PageHeaderBacklink>
    </Box>
  ) : null;

  const showEditIcon = isBoolean(props.showEditIcon) ? props.showEditIcon : !isNil(props.onEdit);

  const editIcon = showEditIcon && (
    <Box marginLeft={1}>
      <PageHeaderEditIconButton onClick={props.onEdit} size="small" variant="circle">
        <EditIcon />
      </PageHeaderEditIconButton>
    </Box>
  );

  const tools = props.children ? <Box>{props.children}</Box> : null;

  return (
    <PageHeaderRoot>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" spacing={2}>
        {backlink}
        <Box flexGrow={1}>
          <Grid container alignItems="center">
            <Grid item container>
              {heading}
              {editIcon}
            </Grid>
          </Grid>
          {perex}
        </Box>
        {tools}
      </Stack>
    </PageHeaderRoot>
  );
};

export default PageHeader;
